import React, { useContext, useEffect, useState } from "react";
import clsx from "clsx";
import DashboardContext from "src/context/dashboard-context";
import { Colors, orderCurrency } from "src/utils/data/constant";
import {
  convertCurrency,
  formatNumber,
  getPortfolioTypeTranslation,
} from "src/utils/helper/functions";
import DoughnutChart from "../../doughnut-chart";

const options = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
  },
};

type CashDistributionProps = {
  setShowCashDetails?: any;
  setShowAssetsDetails?: any;
  setShowFundDetails?: any;
  selectedCashItem: any;
  setSelectedCashItem: any;
  selectedCashItemCurrency: any;
  setSelectedCashItemCurrency: any;
};

const CashDistribution: React.FC<CashDistributionProps> = ({
  setShowCashDetails,
  setShowAssetsDetails,
  setShowFundDetails,
  selectedCashItem,
  setSelectedCashItem,
  selectedCashItemCurrency,
  setSelectedCashItemCurrency,
}: CashDistributionProps) => {
  const {
    selectedAccount,
    selectedPortfolio,
    availableCash,
    sumOfPendingCash,
    assets,
    pendingSubscriptionRequests,
    fxRates,
    countriesList,
  }: any = useContext(DashboardContext);
  const [selectedCurrency, setSelectedCurrency] = useState<any>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [selectedItemChartData, setSelectedItemChartData] = useState<any>(null);

  useEffect(() => {
    setChartData({
      labels: Object.entries(sortedData(getChartByData())).map(
        ([key, value]: any) => key
      ),
      datasets: [
        {
          label: "",
          data:
            Object.entries(sortedData(getChartByData())).map(
              ([key, value]: any) =>
                (
                  +(
                    getSumission(value, "reportingMarketAmount") /
                    (availableCash + sumOfPendingCash)
                  ) * 100
                )?.toFixed(2)
            ) || [],
          backgroundColor: Colors,
          borderColor: Colors,
          borderWidth: 1,
        },
      ],
    });
  }, [assets]);

  const getChartByData = (): any => {
    const groupedData = assets
      .filter((item: any) => item.productGroupCode === "CSH")
      ?.reduce((acc: any, obj: any) => {
        const key = obj["investmentCurrency"];
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(obj);
        return acc;
      }, {});
    return groupedData;
  };

  const getSumission = (data: any[], key: string): number => {
    return data?.reduce(
      (accumulator, currentValue) => accumulator + currentValue[key],
      0
    );
  };

  const sortedData = (data: any) => {
    return Object.keys(data)
      .sort((a, b) => orderCurrency.indexOf(a) - orderCurrency.indexOf(b))
      .reduce((acc: any, key: any) => {
        acc[key] = data[key];
        return acc;
      }, {});
  };

  const onSelectItem = (item: any, currency: string) => {
    setSelectedItemChartData({
      labels: Object.keys(groupedByPortfolio(item)),
      datasets: [
        {
          label: "",
          data: Object.entries(groupedByPortfolio(item)).map(
            ([key, value]: any) => getSumission(value, "reportingMarketAmount")
          ),
          backgroundColor: Colors,
          borderColor: Colors,
          borderWidth: 1,
        },
      ],
    });
    setSelectedCashItemCurrency(currency);
    setSelectedCashItem(item);
  };

  const getSumissionTwo = (
    data: any[],
    key: string,
    currency: string
  ): number => {
    return data?.reduce(
      (accumulator, currentValue) =>
        accumulator +
        (currentValue?.fromAvailableCash?.find(
          (e: any) => e.originalCurrency == currency
        ) ||
          currentValue?.fromAvailableCash?.find(
            (e: any) => e.originalCurrency == currency
          ))[key],
      0
    );
  };

  const groupedByPortfolio = (data: any) => {
    return data.reduce((result: any, item: any) => {
      if (!result[item.portfolioCode]) {
        result[item.portfolioCode] = [];
      }
      result[item.portfolioCode].push(item);
      return result;
    }, {});
  };

  return !selectedCashItem?.length ? (
    <div
      id="csh"
      className=" slide-left p-4 w-full bg-white rounded-xl add-shadow flex-col justify-start items-start gap-5 inline-flex"
    >
      <div className="Frame576281 self-stretch flex-col justify-start items-center gap-3 flex">
        <div className="Frame576049 self-stretch flex-col justify-start items-start gap-5 flex">
          <div className="items-center gap-2 inline-flex w-full">
            <img
              src="/images/arrow-right.svg"
              className={clsx("cursor-pointer", "rotate-180")}
              width={24}
              height={24}
              alt="back"
              onClick={() => {
                setShowCashDetails(false);
                setShowAssetsDetails(false);
                setShowFundDetails(false);
              }}
            />
            <div className=" flex grow text-[#2a2a2a] text-sm md:text-base font-bold">
              Cash
            </div>
          </div>
          <div className=" self-stretch px-3 py-4 bg-white rounded-2xl add-shadow justify-between items-center flex flex-wrap">
            <div className="w-full md:w-[398px] lg:w-full xl:w-[398px] flex-col justify-center items-start gap-5 inline-flex">
              <div className=" flex-col justify-center items-start flex">
                <div className=" opacity-70 text-[#3f3f3f] text-xs xl:text-sm font-semibold leading-snug">
                  Total cash (
                  {selectedPortfolio?.reportingCurrency ||
                    selectedAccount?.reportingCurrency}
                  )
                </div>
                <div className=" text-[#002f6c] text-[25px] xl:text-[32px] font-bold leading-[48px]">
                  {formatNumber(
                    availableCash + Math.abs(sumOfPendingCash),
                    selectedAccount?.reportingCurrencyDcp
                  )}
                </div>
              </div>
              <div className=" self-stretch justify-between items-center inline-flex">
                <div className=" flex-col justify-center items-start inline-flex">
                  <div className=" opacity-70 text-[#2a2a2a] text-xs xl:text-sm font-medium">
                    Available cash
                  </div>
                  <div
                    className={clsx(
                      " text-[#40bd6e] text-xs xl:text-lg font-semibold leading-[48px]",
                      availableCash?.toFixed(0) == 0
                        ? "text-[#637381]"
                        : availableCash > 0
                        ? "text-[#40bd6e]"
                        : "text-red-500"
                    )}
                  >
                    {availableCash?.toFixed(3) == 0
                      ? "-"
                      : formatNumber(
                          availableCash,
                          selectedAccount?.reportingCurrencyDcp
                        )}
                  </div>
                </div>
                <div className=" w-px h-[25px] bg-zinc-400 rounded-[11px]" />
                <div className=" flex-col justify-center items-start inline-flex">
                  <div className="Totalactiveusers opacity-70 text-[#2a2a2a] text-xs xl:text-sm font-medium">
                    Pending settlement
                  </div>
                  <div className="NumbershortRand text-[#2a2a2a] text-xs xl:text-lg font-semibold leading-[48px]">
                    {sumOfPendingCash?.toFixed(3) != 0
                      ? formatNumber(
                          sumOfPendingCash,
                          selectedAccount?.reportingCurrencyDcp
                        )
                      : "-"}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex grow items-center justify-center h-[200px]">
              {chartData ? (
                <DoughnutChart data={chartData} options={options} />
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>
      <p className="text-blue-500 font-semibold text-sm xl:text-base">Cash</p>
      <div className=" self-stretch  flex-col justify-start items-start gap-4 flex px-3 py-4 bg-white rounded-2xl add-shadow">
        {Object.entries(sortedData(getChartByData())).map(
          ([item, value]: any, index: number) => {
            return (
              <div key={index} className="flex flex-col w-full ">
                <div className="self-stretch justify-start items-center gap-2 md:gap-4 inline-flex">
                  <div className=" rounded-[500px] justify-center items-center flex">
                    <div className="w-5 h-5 md:w-10 md:h-10 rounded-[500px] justify-center items-center flex">
                      <div
                        className={clsx(
                          " grow shrink basis-0 self-stretch rounded-[500px]"
                        )}
                        style={{ backgroundColor: Colors[index] }}
                      />
                    </div>
                  </div>
                  <div className=" grow  basis-0 rounded-3xl flex-col justify-start items-start gap-1.5 inline-flex">
                    <div className=" self-stretch justify-between items-center inline-flex">
                      <div className=" h-[31px] justify-start items-center gap-1.5 flex">
                        <div className=" text-blue-500 text-xs md:text-sm font-bold leading-snug">
                          {item} Cash
                        </div>
                        <div className="Stats px-2 py-1 rounded-[32px] justify-start items-center gap-[9px] flex">
                          <div className="Label text-right text-[#002f6c] text-[10px] md:text-xs font-medium">
                            ~{" "}
                            {(
                              (getSumission(value, "reportingMarketAmount") /
                                (availableCash + sumOfPendingCash)) *
                              100
                            )?.toFixed(2)}
                            %
                          </div>
                          <div
                            onClick={() => {
                              selectedCurrency === item
                                ? setSelectedCurrency(null)
                                : setSelectedCurrency(item);
                            }}
                            className=" bg-[#f5f6fa] cursor-pointer rounded-full w-[23px] h-[23px] flex items-center justify-center"
                          >
                            <div className="text-right text-[#002f6c] text-base md:text-lg font-medium">
                              {selectedCurrency === item ? "-" : "+"}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="NameRand text-[#3f3f3f] text-xs md:text-sm font-semibold leading-snug">
                        {formatNumber(
                          getSumission(value, "investmentMarketAmount"),
                          value[0]?.investmentCurrencyDcp
                        )}
                      </div>
                    </div>
                    {item !== selectedAccount?.reportingCurrency &&
                      selectedCurrency !== item && (
                        <div className="w-full self-stretch justify-between items-start gap-1.5 inline-flex">
                          <div
                            className={clsx(
                              " h-[22px] opacity-70 text-[#637381] text-xs md:text-sm font-semibold flex gap-1",
                              "flex-row"
                            )}
                          >
                            <span>{selectedAccount?.reportingCurrency} </span>
                            <span>equivalent </span>
                          </div>
                          <div className=" text-[#637381] text-xs md:text-sm font-semibold opacity-70 leading-snug">
                            {formatNumber(
                              convertCurrency(
                                getSumission(value, "investmentMarketAmount") -
                                  getSumission(
                                    value,
                                    "investmentPendingAmount"
                                  ) -
                                  Math.abs(
                                    getSumissionTwo(
                                      pendingSubscriptionRequests?.filter(
                                        (ele: any) =>
                                          ele?.fromAvailableCash?.find(
                                            (e: any) =>
                                              e.originalCurrency == item
                                          ) &&
                                          ele.status === "Pending" &&
                                          (ele.type === "WITHDRAW" ||
                                            ele.type === "SUBSCRIPTION")
                                      ),
                                      "convertedAmount",
                                      item
                                    ) +
                                      getSumissionTwo(
                                        pendingSubscriptionRequests?.filter(
                                          (ele: any) =>
                                            ele?.fromAvailableCash?.find(
                                              (e: any) =>
                                                e.originalCurrency == item
                                            ) &&
                                            ele.status === "Pending" &&
                                            (ele.type === "CASH_TRANSFER" ||
                                              ele.type ===
                                                "CURRENCY_CONVERSION")
                                        ),
                                        "originalAmount",
                                        item
                                      )
                                  ),
                                item,
                                selectedAccount?.reportingCurrency,
                                fxRates
                              ) as number,
                              selectedAccount?.reportingCurrencyDcp
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                  <div
                    className="cursor-pointer w-6 h-6 justify-center items-center flex"
                    onClick={() => {
                      onSelectItem(value, item);
                    }}
                  >
                    <img
                      src="/images/arrow-right.svg"
                      className={clsx("cursor-pointer")}
                      width={24}
                      height={24}
                      alt="arrow"
                    />
                  </div>
                </div>
                {selectedCurrency === item && (
                  <div className="flex flex-col mb-1 ps-[55px] pe-[40px] mt-1">
                    <div className="opacity-70 flex items-center justify-between mb-2">
                      <span className=" text-[#637381] text-sm font-medium">
                        Available cash
                      </span>
                      <p className="text-[#637381] text-sm font-semibold leading-snug">
                        {formatNumber(
                          getSumission(value, "investmentMarketAmount") -
                            getSumission(value, "investmentPendingAmount") -
                            Math.abs(
                              getSumissionTwo(
                                pendingSubscriptionRequests?.filter(
                                  (ele: any) =>
                                    ele?.fromAvailableCash?.find(
                                      (e: any) => e.originalCurrency == item
                                    ) &&
                                    ele.status === "Pending" &&
                                    (ele.type === "WITHDRAW" ||
                                      ele.type === "SUBSCRIPTION")
                                ),
                                "convertedAmount",
                                item
                              ) +
                                getSumissionTwo(
                                  pendingSubscriptionRequests?.filter(
                                    (ele: any) =>
                                      ele?.fromAvailableCash?.find(
                                        (e: any) => e.originalCurrency == item
                                      ) &&
                                      ele.status === "Pending" &&
                                      (ele.type === "CASH_TRANSFER" ||
                                        ele.type === "CURRENCY_CONVERSION")
                                  ),
                                  "originalAmount",
                                  item
                                )
                            ),
                          value[0]?.investmentCurrencyDcp,
                          false
                        )}
                      </p>
                    </div>
                    <div className="flex items-center justify-between ">
                      <span className="opacity-70 text-[#637381] text-sm font-medium">
                        Pending settlement
                      </span>
                      <p className="text-[#637381] text-sm font-semibold leading-snug">
                        {formatNumber(
                          getSumission(value, "investmentPendingAmount") +
                            Math.abs(
                              getSumissionTwo(
                                pendingSubscriptionRequests?.filter(
                                  (ele: any) =>
                                    ele?.fromAvailableCash?.find(
                                      (e: any) => e.originalCurrency == item
                                    ) &&
                                    ele.status === "Pending" &&
                                    (ele.type === "WITHDRAW" ||
                                      ele.type === "SUBSCRIPTION")
                                ),
                                "convertedAmount",
                                item
                              ) +
                                getSumissionTwo(
                                  pendingSubscriptionRequests?.filter(
                                    (ele: any) =>
                                      ele?.fromAvailableCash?.find(
                                        (e: any) => e.originalCurrency == item
                                      ) &&
                                      ele.status === "Pending" &&
                                      (ele.type === "CASH_TRANSFER" ||
                                        ele.type === "CURRENCY_CONVERSION")
                                  ),
                                  "originalAmount",
                                  item
                                )
                            ),
                          value[0]?.investmentCurrencyDcp
                        )}
                      </p>
                    </div>
                    {item !== selectedAccount?.reportingCurrency && (
                      <div className=" self-stretch justify-between items-start gap-1.5 inline-flex mt-2">
                        <div
                          className={clsx(
                            " h-[22px] opacity-70 text-[#637381] text-xs md:text-sm font-semibold flex gap-1",
                            "flex-row"
                          )}
                        >
                          <span>{selectedAccount?.reportingCurrency} </span>
                          <span>equivalent </span>
                        </div>
                        <div className=" text-[#637381] text-sm font-semibold opacity-70 leading-snug">
                          {formatNumber(
                            convertCurrency(
                              getSumission(value, "investmentMarketAmount") -
                                getSumission(value, "investmentPendingAmount") -
                                Math.abs(
                                  getSumissionTwo(
                                    pendingSubscriptionRequests?.filter(
                                      (ele: any) =>
                                        ele?.fromAvailableCash?.find(
                                          (e: any) => e.originalCurrency == item
                                        ) &&
                                        ele.status === "Pending" &&
                                        (ele.type === "WITHDRAW" ||
                                          ele.type === "SUBSCRIPTION")
                                    ),
                                    "convertedAmount",
                                    item
                                  ) +
                                    getSumissionTwo(
                                      pendingSubscriptionRequests?.filter(
                                        (ele: any) =>
                                          ele?.fromAvailableCash?.find(
                                            (e: any) =>
                                              e.originalCurrency == item
                                          ) &&
                                          ele.status === "Pending" &&
                                          (ele.type === "CASH_TRANSFER" ||
                                            ele.type === "CURRENCY_CONVERSION")
                                      ),
                                      "originalAmount",
                                      item
                                    )
                                ),
                              item,
                              selectedAccount?.reportingCurrency,
                              fxRates
                            ) as number,
                            selectedAccount?.reportingCurrencyDcp
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          }
        )}
        <div className=" self-stretch origin-top-left rotate-90 opacity-30  rounded-[11px]" />
      </div>
    </div>
  ) : (
    <div className=" slide-left p-4 w-full bg-white rounded-xl add-shadow flex-col justify-start items-start gap-5 inline-flex">
      <div className="Frame576281 self-stretch flex-col justify-start items-center gap-3 flex">
        <div className="Frame576049 self-stretch flex-col justify-start items-start gap-5 flex">
          <div className="items-center gap-2 inline-flex w-full">
            <img
              src="/images/arrow-right.svg"
              className={clsx("cursor-pointer", "rotate-180")}
              width={24}
              height={24}
              alt="back"
              onClick={() => {
                setSelectedCashItem(null);
                setSelectedCashItemCurrency(null);
              }}
            />
            <div className=" flex grow text-[#2a2a2a] text-base font-bold">
              Cash
            </div>
          </div>
          <div className=" self-stretch px-3 py-4 bg-white rounded-2xl add-shadow justify-between items-center flex flex-wrap">
            <div className="w-full md:w-[398px] lg:w-full xl:w-[398px] flex-col justify-center items-start gap-5 inline-flex">
              <div className=" flex-col justify-center items-start flex">
                <div className="Totalactiveusers opacity-70 text-[#3f3f3f] text-xs xl:text-sm font-semibold leading-snug">
                  Total cash ({selectedCashItemCurrency})
                </div>
                <div className="NumbershortRand text-[#002f6c] text-[25px] xl:text-[32px] font-bold leading-[48px]">
                  {formatNumber(
                    getSumission(selectedCashItem, "investmentMarketAmount"),
                    selectedCashItem[0]?.investmentCurrencyDcp
                  )}
                </div>
              </div>
              <div className="grow self-stretch justify-between items-center inline-flex">
                <div className=" flex-col justify-center items-start inline-flex">
                  <div className="Totalactiveusers opacity-70 text-[#2a2a2a] text-xs md:text-sm font-medium">
                    Available cash
                  </div>
                  <div className="NumbershortRand text-[#40bd6e] text-xs md:text-lg font-semibold leading-[48px]">
                    {formatNumber(
                      getSumission(selectedCashItem, "investmentMarketAmount") -
                        getSumission(
                          selectedCashItem,
                          "investmentPendingAmount"
                        ) -
                        Math.abs(
                          getSumissionTwo(
                            pendingSubscriptionRequests?.filter(
                              (ele: any) =>
                                ele?.fromAvailableCash?.find(
                                  (e: any) =>
                                    e.originalCurrency ==
                                    selectedCashItemCurrency
                                ) &&
                                ele.status === "Pending" &&
                                (ele.type === "WITHDRAW" ||
                                  ele.type === "SUBSCRIPTION")
                            ),
                            "convertedAmount",
                            selectedCashItemCurrency
                          ) +
                            getSumissionTwo(
                              pendingSubscriptionRequests?.filter(
                                (ele: any) =>
                                  ele?.fromAvailableCash?.find(
                                    (e: any) =>
                                      e.originalCurrency ==
                                      selectedCashItemCurrency
                                  ) &&
                                  ele.status === "Pending" &&
                                  (ele.type === "CASH_TRANSFER" ||
                                    ele.type === "CURRENCY_CONVERSION")
                              ),
                              "originalAmount",
                              selectedCashItemCurrency
                            )
                        ),
                      selectedCashItem[0]?.investmentCurrencyDcp
                    )}
                  </div>
                </div>
                <div className=" w-px h-[25px] bg-zinc-400 rounded-[11px]" />
                <div className=" flex-col justify-center items-start inline-flex">
                  <div className="Totalactiveusers opacity-70 text-[#2a2a2a] text-xs md:text-sm font-medium">
                    Pending settlement
                  </div>
                  <div className="NumbershortRand text-[#2a2a2a] text-xs md:text-lg font-semibold leading-[48px]">
                    {formatNumber(
                      getSumission(
                        selectedCashItem,
                        "investmentPendingAmount"
                      ) +
                        Math.abs(
                          getSumissionTwo(
                            pendingSubscriptionRequests?.filter(
                              (ele: any) =>
                                ele?.fromAvailableCash?.find(
                                  (e: any) =>
                                    e.originalCurrency ==
                                    selectedCashItemCurrency
                                ) &&
                                ele.status === "Pending" &&
                                (ele.type === "WITHDRAW" ||
                                  ele.type === "SUBSCRIPTION")
                            ),
                            "convertedAmount",
                            selectedCashItemCurrency
                          ) +
                            getSumissionTwo(
                              pendingSubscriptionRequests?.filter(
                                (ele: any) =>
                                  ele?.fromAvailableCash?.find(
                                    (e: any) =>
                                      e.originalCurrency ==
                                      selectedCashItemCurrency
                                  ) &&
                                  ele.status === "Pending" &&
                                  (ele.type === "CASH_TRANSFER" ||
                                    ele.type === "CURRENCY_CONVERSION")
                              ),
                              "originalAmount",
                              selectedCashItemCurrency
                            )
                        ),
                      selectedCashItem[0]?.investmentCurrencyDcp
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex grow items-center justify-center h-[200px]">
              {chartData ? (
                <DoughnutChart data={selectedItemChartData} options={options} />
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>
      <p className="text-blue-500 font-semibold text-sm xl:text-base">
        Portfolio distribution
      </p>
      <div className="Stack self-stretch  flex-col justify-start items-start gap-4 flex px-3 py-4 bg-white rounded-2xl add-shadow">
        {Object.entries(groupedByPortfolio(selectedCashItem)).map(
          ([key, value]: any, index) => {
            return (
              <div key={index} className="flex flex-col w-full ">
                <div className="self-stretch justify-start items-start gap-2 md:gap-4 inline-flex">
                  <div className=" rounded-[500px] justify-center items-center flex">
                    <div className="w-5 h-5 md:w-10 md:h-10 rounded-[500px] justify-center items-center flex">
                      <div
                        className={clsx(
                          " grow shrink basis-0 self-stretch  rounded-[500px]"
                        )}
                        style={{ backgroundColor: Colors[index] }}
                      />
                    </div>
                  </div>
                  <div className=" grow shrink basis-0 rounded-3xl flex-col justify-start items-start gap-1.5 inline-flex">
                    <div className=" self-stretch justify-between items-center inline-flex">
                      <div className=" h-[31px] justify-start items-center gap-1.5 flex">
                        <div className=" text-blue-500 text-[10px] md:text-sm font-bold leading-snug">
                          {key}
                        </div>
                        <div className="Stats px-2 py-1 rounded-[32px] justify-start items-center gap-[9px] flex">
                          <div className="Label text-right text-[#002f6c] text-[9px] md:text-xs font-medium">
                            ~{" "}
                            {(
                              (getSumission(value, "reportingMarketAmount") /
                                getSumission(
                                  selectedCashItem,
                                  "reportingMarketAmount"
                                )) *
                              100
                            )?.toFixed(2)}
                            %
                          </div>
                        </div>
                      </div>
                      <div className=" text-[#3f3f3f] text-[10px] md:text-sm font-semibold leading-snug">
                        {formatNumber(
                          getSumission(value, "investmentMarketAmount"),
                          value[0]?.investmentCurrencyDcp
                        )}
                      </div>
                    </div>
                    <div className=" text-blue-500 text-xs md:text-sm font-semibold leading-snug">
                      {getPortfolioTypeTranslation(
                        value[0]?.portfolioGroupName
                          ?.split("Portfolio")[0]
                          ?.trim()
                      )}
                    </div>
                    <div className="flex items-center justify-between w-full">
                      <span className=" text-[#637381] text-xs md:text-sm font-medium">
                        Available cash
                      </span>
                      <p className="text-[#637381] text-xs md:text-sm font-semibold leading-snug">
                        {formatNumber(
                          getSumission(value, "investmentMarketAmount") -
                            getSumission(value, "investmentPendingAmount") -
                            Math.abs(
                              getSumissionTwo(
                                pendingSubscriptionRequests?.filter(
                                  (request: any) =>
                                    request?.fromAvailableCash?.find(
                                      (e: any) =>
                                        e.originalCurrency ==
                                        selectedCashItemCurrency
                                    ) &&
                                    request.portfolioCode == key &&
                                    request.status === "Pending" &&
                                    (request.type === "WITHDRAW" ||
                                      request.type === "SUBSCRIPTION")
                                ),
                                "convertedAmount",
                                selectedCashItemCurrency
                              ) +
                                getSumissionTwo(
                                  pendingSubscriptionRequests?.filter(
                                    (request: any) =>
                                      request?.fromAvailableCash?.find(
                                        (e: any) =>
                                          e.originalCurrency ==
                                          selectedCashItemCurrency
                                      ) &&
                                      request.sourcePortfolio?.accountCode ==
                                        key &&
                                      request.status === "Pending" &&
                                      (request.type === "CASH_TRANSFER" ||
                                        request.type === "CURRENCY_CONVERSION")
                                  ),
                                  "originalAmount",
                                  selectedCashItemCurrency
                                )
                            ),
                          value[0]?.investmentCurrencyDcp
                        )}
                      </p>
                    </div>
                    <div className="flex items-center justify-between w-full">
                      <span className=" text-[#637381] text-xs md:text-sm font-medium">
                        Pending settlement
                      </span>
                      <p className="text-[#637381] text-xs md:text-sm font-semibold leading-snug">
                        {formatNumber(
                          getSumission(value, "investmentPendingAmount") +
                            Math.abs(
                              getSumissionTwo(
                                pendingSubscriptionRequests?.filter(
                                  (request: any) =>
                                    request?.fromAvailableCash?.find(
                                      (e: any) =>
                                        e.originalCurrency ==
                                        selectedCashItemCurrency
                                    ) &&
                                    request.portfolioCode == key &&
                                    request.status === "Pending" &&
                                    (request.type === "WITHDRAW" ||
                                      request.type === "SUBSCRIPTION")
                                ),
                                "convertedAmount",
                                selectedCashItemCurrency
                              ) +
                                getSumissionTwo(
                                  pendingSubscriptionRequests?.filter(
                                    (request: any) =>
                                      request?.fromAvailableCash?.find(
                                        (e: any) =>
                                          e.originalCurrency ==
                                          selectedCashItemCurrency
                                      ) &&
                                      request.sourcePortfolio?.accountCode ==
                                        key &&
                                      request.status === "Pending" &&
                                      (request.type === "CASH_TRANSFER" ||
                                        request.type === "CURRENCY_CONVERSION")
                                  ),
                                  "originalAmount",
                                  selectedCashItemCurrency
                                )
                            ),
                          value[0]?.investmentCurrencyDcp
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
        )}
        <div className=" self-stretch origin-top-left rotate-90 opacity-30  rounded-[11px]" />
      </div>
    </div>
  );
};

export default CashDistribution;
